import {getCookie} from '@utils/cookies';
import {useCallback, useEffect, useState} from 'react';

import {FAILED_AUTH_INDEX} from '@web-catalog/const/numbers';

import {ProfileType} from '@web-catalog/types';

import {useOmicronClientFlag} from '@web-catalog/hooks/omicron';

import {
  useSetAuthorizedProfile,
  useSetAuthProgressState,
} from '@web-catalog/store/auth';

import {setVkIdForAnalytics} from '@web-catalog/utils/analytics';
import {useAuthAnalytics} from '@web-catalog/utils/analytics/useAuthAnalytics';

import IframeWrapper from './IframeWrapper';

export const AuthController = () => {
  const canAutologin = useOmicronClientFlag('WEB_AUTO_LOGIN') !== 'none';
  const setAuthProgressState = useSetAuthProgressState();
  const setAuthorizedProfile = useSetAuthorizedProfile();
  const [shouldOpenIframe, setShouldOpenIframe] = useState(false);
  const {logAuthorizationAutologinError} = useAuthAnalytics();

  useEffect(() => {
    if (!canAutologin) {
      return;
    }

    setAuthProgressState('in_progress');

    if (getCookie('has_token')) {
      // already have cookie possibly, will get profile
      fetch('/catalog/api/auth-with-token', {cache: 'no-store'})
        .then(async (r) => r.json())
        .then(({response}) => {
          setAuthorizedProfile(response);
          setVkIdForAnalytics(
            (response as ProfileType)?.id ?? FAILED_AUTH_INDEX,
          );
          setAuthProgressState('notify_user');
        })
        .catch(() => setShouldOpenIframe(true));
    } else {
      setShouldOpenIframe(true);
    }
  }, [canAutologin, setAuthorizedProfile, setAuthProgressState]);

  const onIframeError = useCallback(
    (errorText?: string) => {
      setShouldOpenIframe(false);
      setAuthProgressState('failed');

      logAuthorizationAutologinError(errorText ?? 'unknown error');
    },
    [logAuthorizationAutologinError, setAuthProgressState],
  );

  const onIframeSuccess = useCallback(
    (profile: ProfileType) => {
      setShouldOpenIframe(false);
      setAuthorizedProfile(profile);
      setVkIdForAnalytics(profile.id ?? FAILED_AUTH_INDEX);
      setAuthProgressState('notify_user');
    },
    [setAuthorizedProfile, setAuthProgressState],
  );

  if (!shouldOpenIframe) {
    return null;
  }

  return <IframeWrapper onSuccess={onIframeSuccess} onError={onIframeError} />;
};
